import React, {ChangeEvent, FocusEvent, KeyboardEvent, ReactNode, useEffect, useState} from "react";
import "./AutoCompleteInput.css";

export interface SuggestionSection<S> {
  id: string;
  header: ReactNode | null;
  suggestions: Suggestion<S>[];
}

interface Suggestion<S> {
  id: string;
  value: S;
  label: string;
}

interface Props<S> {
  text: string;
  onTextChange: (value: string) => void;
  onChange: (value: S | null) => void;
  placeHolder?: string;
  placeHolderIconClass?: string
  className?: string;
  popOverPosition?: "below" | "above";
  suggestionSections: SuggestionSection<S>[];
  onHoverSuggestion?: (value: S | null) => void;
}

export const AutoCompleteInput = <S extends any>({
                                                   text,
                                                   onTextChange,
                                                   placeHolder,
                                                   placeHolderIconClass,
                                                   onChange,
                                                   className,
                                                   suggestionSections,
                                                   popOverPosition = "below",
                                                   onHoverSuggestion
                                                 }: Props<S>) => {
  const [focused, setFocused] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState<Suggestion<S> | null>(null);
  const allSuggestions: Suggestion<S>[] = suggestionSections.reduce((acc, section) => [...acc, ...section.suggestions],
      [] as Suggestion<S>[]);

  useEffect(() => {
    setActiveSuggestion(null);
  }, [suggestionSections])

  function handleBlur(event: FocusEvent) {
    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof HTMLDivElement &&
        relatedTarget.className.indexOf("auto-complete-input-popover-line") >= 0
    ) {
      // clicked on a suggestion, don't blur input
      return;
    }
    if (activeSuggestion) {
      onTextChange(activeSuggestion.label);
      onChange(activeSuggestion.value);
    }
    setFocused(false);
  }

  function handleKeyUp(event: KeyboardEvent<HTMLInputElement>) {
    if (allSuggestions.length === 0) {
      return;
    }

    const activeIdx = allSuggestions.findIndex(sugg => sugg === activeSuggestion);

    if (event.key === "Tab" || event.key === "Enter") {
      if (activeSuggestion) {
        onTextChange(activeSuggestion.label);
        onChange(activeSuggestion.value);
        setFocused(false);
        (event.target as HTMLInputElement).blur();
      }
    } else if (event.key === "ArrowDown") {
      const newActive = allSuggestions[(activeIdx + 1) % allSuggestions.length];
      setActiveSuggestion(newActive);
    } else if (event.key === "ArrowUp") {
      if (activeIdx < 0) {
        const newActive = allSuggestions[allSuggestions.length - 1];
        setActiveSuggestion(newActive);
      } else {
        const newActive = allSuggestions[(activeIdx - 1 + allSuggestions.length) % allSuggestions.length];
        setActiveSuggestion(newActive);
      }
    }
  }

  function handleTextChange(event: ChangeEvent<HTMLInputElement>) {
    const txt = event.target.value;
    onTextChange(txt);
    if (txt === "") {
      onChange(null);
    }
  }

  function handleClear() {
    onTextChange("");
    onChange(null);
  }

  const popOverPositionClazz = popOverPosition === "above" ? " above" : "";
  const hasText = text !== "";

  return (
      <div className={"auto-complete-input-container " + (className ?? "")}>
        {hasText ? (
            <span
                className="auto-complete-icon clickable lcd-icon lcd-icon-close"
                onClick={handleClear}
            />
        ) : (
             placeHolderIconClass && <span className={"auto-complete-icon lcd-icon " + placeHolderIconClass}/>
         )}
        <input
            className="auto-complete-input"
            value={text}
            placeholder={placeHolder}
            onChange={handleTextChange}
            onFocus={() => {
              setFocused(true);
            }}
            onKeyUp={handleKeyUp}
            onBlur={handleBlur}
        />
        {focused && allSuggestions.length > 0 && (
            <div
                className={
                  "auto-complete-input-popover" +
                  (popOverPositionClazz ? popOverPositionClazz : "")
                }
            >
              {suggestionSections.map(section => {
                if (section.suggestions.length === 0) {
                  return null;
                }
                return (
                    <div className="auto-complete-section" key={section.id}>
                      {section.header && (
                          <div className="auto-complete-section-header">
                            {section.header}
                          </div>
                      )}
                      {section.suggestions.map(suggestion => {
                        const isActive = activeSuggestion === suggestion;
                        return (
                            <div
                                key={suggestion.id}
                                className={"auto-complete-input-popover-line" + (isActive ? " active-suggestion" : "")}
                                tabIndex={-1}
                                onClick={() => {
                                  setActiveSuggestion(null);
                                  onTextChange(suggestion.label);
                                  onChange(suggestion.value);
                                  setFocused(false);
                                }}
                                onMouseOver={() => {
                                  if (onHoverSuggestion) {
                                    onHoverSuggestion(suggestion.value);
                                  }
                                }}
                                onMouseOut={() => {
                                  if (onHoverSuggestion) {
                                    onHoverSuggestion(null);
                                  }
                                }}
                            >
                              {suggestion.label}
                            </div>
                        );
                      })}
                    </div>
                );
              })}
            </div>
        )}
      </div>
  );
};
